import { Injectable } from "@angular/core";
import { AngularFirestore } from "@angular/fire/compat/firestore";
import * as fAuth from "firebase/auth";
import { Auth } from "@angular/fire/auth";
import { MatSnackBar } from "@angular/material/snack-bar";
import { changePage, connLink, generateId, getCurrentDateForDB, presentLoading, presentSnackBar } from "src/global";
import { BehaviorSubject, Subject } from "rxjs";
import { Router } from "@angular/router";
import { User } from "node_js/models/tables/user";
import { PagesEnum } from "node_js/models/enums/pages";
import { SnackBarColor } from "node_js/models/enums/snackbar-color";
import { LanguageEnum } from "node_js/models/enums/language";
import { HttpClient } from "@angular/common/http";
import { UserComplete } from "node_js/models/business/user-complete";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { UserSettingsComponent } from '../components/dialogs/user-settings/user-settings.component';
import { FailedResponse } from "node_js/models/failed-response.model";
import { Device } from "@capacitor/device";
import { PushNotificationService } from "./push-notification.service";
import { UserSettingsComplete } from "node_js/models/business/user-settings-complete";
import { LabService } from "./lab.service";
import { GadgetService } from "./gadget.service";
import { Capacitor } from "@capacitor/core";
import { DeviceTypeEnum } from "node_js/models/enums/device-type";
import { SiteRoleEnum } from "node_js/models/enums/site-role";


@Injectable({
    providedIn: "root"
})
export class UserAuthService {

    didLoginCheck = false;
    loggedIn = false;

    fAuthStateChangedObservable: BehaviorSubject<any>;

    creatingUserNickname = "";

    currentUserComplete: UserComplete = null;

    currentLoadingRef: MatDialogRef<unknown>;
    currentLang: LanguageEnum = LanguageEnum.ENGLISH;

    fAuthUser: fAuth.User = null;

    // Store intended route after login (e.g., for delete-user-data page)
    pendingRedirectRoute: string | null = null;

    constructor(
        private afs: AngularFirestore,
        private matSnackBar: MatSnackBar,
        private router: Router,
        private http: HttpClient,
        private matDialog: MatDialog,
        private pushNotificationService: PushNotificationService,
        private labService: LabService,
        private gadgetService: GadgetService,
        private auth: Auth
    ) {
        this.fAuthStateChangedObservable = new BehaviorSubject(null);
    }



    /**
     * Starts the authentication check - should be called after splash screen has begun
     */
    public startAuthCheck() {
        // Check if Auth is properly initialized
        if (!this.auth) {
            console.error('[UserAuth] Auth instance is not available');
            presentSnackBar("Firebase Auth is not configured. Please check your Firebase configuration.", SnackBarColor.DANGER, this.matSnackBar);
            return;
        }

        try {
            fAuth.onAuthStateChanged(this.auth, (fAuthUser: fAuth.User) => {
                this.fAuthUser = fAuthUser;

                // Check if user is trying to access delete-user-data or privacy-policy page while logged out
                const currentRoute = this.router.url;
                const isOnDeleteUserDataPage = currentRoute.includes('/delete-user-data');
                const isOnPrivacyPolicyPage = currentRoute.includes('/privacy-policy');

                // Previously called reloadUserData(fAuthUser), now just emit the observable and handle state minimally
                if (!fAuthUser) {
                    this.currentUserComplete = null;
                    this.loggedIn = false;
                    this.didLoginCheck = true;
                    this.fAuthStateChangedObservable.next(fAuthUser);
                    this.pushNotificationService.checkPermissions(this);

                    // If trying to access delete-user-data page while logged out, show message and store intended route
                    if (isOnDeleteUserDataPage) {
                        presentSnackBar("You must login before you can delete your account", SnackBarColor.DANGER, this.matSnackBar);
                        this.pendingRedirectRoute = '/delete-user-data';
                    }

                    // Don't redirect away from privacy-policy page
                    if (isOnPrivacyPolicyPage) {
                        this.closeLoading();
                        return;
                    }

                    changePage(PagesEnum.WELCOME, this.router);
                    this.closeLoading();
                    return;
                }
                this.loggedIn = true;
                if (!this.currentLoadingRef) {
                    this.beginLoading();
                }
                this.getUserCompleteFromServer().then((userComplete) => {
                    console.log("user complete", userComplete);
                    this.checkProfileCompletion();
                }).catch(() => {
                    presentSnackBar("Cannot connect to server", SnackBarColor.DANGER, this.matSnackBar);
                    this.loggedIn = false;
                    this.signOut();
                }).finally(() => {
                    this.closeLoading();
                    if (this.loggedIn) {
                        // Check if user is on delete-user-data or privacy-policy page - don't redirect if they are
                        const currentRoute = this.router.url;
                        const isOnDeleteUserDataPage = currentRoute.includes('/delete-user-data');
                        const isOnPrivacyPolicyPage = currentRoute.includes('/privacy-policy');

                        if (isOnDeleteUserDataPage) {
                            console.log("User is on delete-user-data page, not redirecting to dashboard");
                            // Don't redirect - let them stay on the delete-user-data page
                        } else if (isOnPrivacyPolicyPage) {
                            console.log("User is on privacy-policy page, not redirecting");
                            // Don't redirect - let them stay on the privacy-policy page
                        } else if (this.pendingRedirectRoute) {
                            // Redirect to the pending route (e.g., delete-user-data after login)
                            console.log("Redirecting to pending route:", this.pendingRedirectRoute);
                            this.router.navigate([this.pendingRedirectRoute]);
                            this.pendingRedirectRoute = null;
                        } else {
                            console.log("navigating to dashboard");
                            changePage(PagesEnum.DASHBOARD, this.router);
                        }
                    } else {
                        // Don't redirect away from privacy-policy page even when logged out
                        const currentRoute = this.router.url;
                        const isOnPrivacyPolicyPage = currentRoute.includes('/privacy-policy');
                        if (!isOnPrivacyPolicyPage) {
                            changePage(PagesEnum.WELCOME, this.router);
                        }
                    }
                    this.didLoginCheck = true;
                    this.fAuthStateChangedObservable.next(fAuthUser);
                    this.pushNotificationService.checkPermissions(this);
                });
            }, (error) => {
                // Error callback for onAuthStateChanged
                console.error('[UserAuth] Error in onAuthStateChanged:', error);
                if (error && (error as any).code === 'auth/configuration-not-found') {
                    presentSnackBar("Firebase Auth configuration error. Please check your Firebase project settings.", SnackBarColor.DANGER, this.matSnackBar);
                }
            });
        } catch (error) {
            console.error('[UserAuth] Error in startAuthCheck:', error);
            if (error && (error as any).code === 'auth/configuration-not-found') {
                presentSnackBar("Firebase Auth configuration error. Please check your Firebase project settings.", SnackBarColor.DANGER, this.matSnackBar);
            }
        }
    }

    /**
     * Fetches the user complete object from the backend. Does not update state or handle navigation.
     * Returns a Promise<UserComplete>.
     */
    public async getUserCompleteFromServer(forceRefreshToken = false): Promise<UserComplete> {
        if (this.fAuthUser === undefined) {
            this.fAuthUser = this.auth.currentUser;
        }
        if (!this.fAuthUser) {
            return Promise.reject('No authenticated user');
        }
        try {
            let idToken = await this.createIdToken(forceRefreshToken);
            const deviceId = (await Device.getId()).identifier;
            const platform = Capacitor.getPlatform() as DeviceTypeEnum;

            // 🔥 CRITICAL: For iOS, wait for FCM token to be available
            // For web platform, skip FCM token retrieval
            let pushToken: string;
            if (platform === DeviceTypeEnum.WEB) {
                console.log('🌐 [UserAuth] Web platform detected, skipping FCM token retrieval');
                pushToken = "";
            } else if (platform === DeviceTypeEnum.IOS) {
                console.log('🔥 [UserAuth] Waiting for iOS FCM token...');
                pushToken = await this.pushNotificationService.waitForIosFCMToken();
                console.log('🔥 [UserAuth] iOS FCM token received:', pushToken);
            } else {
                pushToken = await this.pushNotificationService.getPushToken(true, this);
            }

            // Only require push token for non-web platforms
            if (platform !== DeviceTypeEnum.WEB && !pushToken) {
                console.error('❌ [UserAuth] Failed to get push token for platform:', platform);
                return Promise.reject('Failed to get push token');
            }

            if (pushToken) {
                console.log('🔥 [UserAuth] Using push token:', pushToken);
            } else {
                console.log('🌐 [UserAuth] No push token for web platform');
            }

            return new Promise<UserComplete>((resolve, reject) => {
                this.http.post(connLink + "/getOrCreateUserComplete", { pushToken, platform, uid: this.fAuthUser.uid, idToken, deviceId, languageId: this.currentLang }).subscribe(
                    (response: any) => {
                        this.currentUserComplete = response as UserComplete;
                        console.log("current user complete", this.currentUserComplete);
                        this.labService.convertLabImages(this.currentUserComplete.labsComplete, this.gadgetService);

                        // 🔥 CRITICAL: Update the userDevice.pushToken with the real token (only for non-web platforms)
                        if (this.currentUserComplete.userDevice && platform !== DeviceTypeEnum.WEB) {
                            this.currentUserComplete.userDevice.pushToken = pushToken;
                            console.log('🔥 [UserAuth] Updated userDevice.pushToken:', pushToken);
                        } else if (platform === DeviceTypeEnum.WEB) {
                            console.log('🌐 [UserAuth] Web platform - no push token to update');
                        }

                        resolve(response as UserComplete);
                    },
                    (errorObj) => {
                        reject(errorObj);
                    }
                );
            });
        } catch (err) {
            return Promise.reject(err);
        }
    }


    updateUserDevice() {
        return new Promise<void>(async (resolve, reject) => {
            let idToken = await this.createIdToken();
            this.http.post(connLink + "/updateUserDevice", { uid: this.currentUserComplete.user.authUid, idToken, userDevice: this.currentUserComplete.userDevice }).subscribe(
                (response: any) => {
                    console.log(response);
                    resolve();
                },
                (errorObj) => {
                    console.log("error", errorObj);
                    reject();
                }
            )
        })
    }

    updateLastLanguageId(languageId: LanguageEnum) {

        return new Promise<void>(async (resolve, reject) => {
            let idToken = await this.createIdToken();
            this.http.post(connLink + "/updateUserLastLanguageId", { uid: this.currentUserComplete.user.authUid, idToken, languageId }).subscribe(
                (response: any) => {
                    resolve();
                },
                (errorObj) => {
                    console.log("error", errorObj);



                    reject();
                }
            )
        })

    }

    private checkProfileCompletion() {
        if (!this.currentUserComplete?.user) return;

        const exemptRoutes = ['/privacy-policy', '/delete-user-data'];
        if (exemptRoutes.some(r => this.router.url.startsWith(r))) return;

        const user = this.currentUserComplete.user;
        const isIncomplete = !user.firstName?.trim() || !user.lastName?.trim();

        if (isIncomplete) {
            console.log('[UserAuth] Profile incomplete, opening forced settings dialog');
            // Small delay to ensure the page has navigated first
            setTimeout(() => {
                this.matDialog.open(UserSettingsComponent, {
                    height: '600px',
                    width: '370px',
                    maxWidth: '93vw',
                    disableClose: true,
                    data: { forceMode: true },
                    autoFocus: false,
                });
            }, 500);
        }
    }


    getUsersSnapshot() {
        return this.afs.collection<User>("users").snapshotChanges();
    }

    signIn(email: string, password: string) {
        this.beginLoading();
        return new Promise<void>((resolve, reject) => {
            fAuth.signInWithEmailAndPassword(this.auth, email, password).then((userInfo) => {
                console.log("signed in")
                resolve();

            }).catch((error) => {
                this.closeLoading();
                let errorCode = error.code;
                if (errorCode == "auth/invalid-login-credentials" || errorCode == "auth/invalid-credential") {
                    presentSnackBar("Email/Password is incorrect", SnackBarColor.DANGER, this.matSnackBar, "", 3000);
                }
                else if (errorCode == "auth/invalid-email") {
                    presentSnackBar("Invalid Email", SnackBarColor.DANGER, this.matSnackBar, "", 3000);
                }
                else if (errorCode == "auth/missing-password") {
                    presentSnackBar("Invalid Password", SnackBarColor.DANGER, this.matSnackBar, "", 3000);
                }
                else {
                    presentSnackBar("There was an error logging in!", SnackBarColor.DANGER, this.matSnackBar, "", 3000);
                }

                reject(errorCode);
            })
        })
    }



    /**
 * Handle Google SDK sign-in result (for mobile platforms)
 */
    async handleGoogleSDKSignIn(googleUserData: any): Promise<void> {
        try {
            console.log("[UserAuth] Handling Google SDK sign-in result...");

            // Extract user information from Google SDK result
            const userData = {
                email: googleUserData.email,
                firstName: googleUserData.firstName,
                lastName: googleUserData.lastName,
                googleId: googleUserData.id,
                googleAccessToken: googleUserData.googleAccessToken,
                googleIdToken: googleUserData.googleIdToken
            };

            console.log("[UserAuth] User data from Google SDK:", userData);

            // Create a Google credential using the ID token from Google SDK
            // This is the same approach the web version uses
            const credential = fAuth.GoogleAuthProvider.credential(
                userData.googleIdToken,  // ID token
                userData.googleAccessToken  // Access token
            );

            console.log("[UserAuth] Created Google credential, signing in with Firebase...");

            // Sign in with the credential to create a real Firebase user
            // This is exactly what the web version does with signInWithPopup
            const userCredential = await fAuth.signInWithCredential(this.auth, credential);

            console.log("[UserAuth] Successfully signed in with Firebase:", userCredential.user);

            // Now handle the sign-in result the same way web OAuth does
            // This will create the user profile and sign them in properly
            await this.handleGoogleSignInResult(userCredential);

            console.log("[UserAuth] Google SDK sign-in completed successfully!");

        } catch (error) {
            console.error("[UserAuth] Error handling Google SDK sign-in:", error);
            throw error;
        }
    }

    /**
     * Handle Google sign-in result from Firebase
     */
    public async handleGoogleSignInResult(result: any): Promise<void> {
        console.log("Signed in with Google:", result.user);

        // Check if this is a new user
        const userCredential = result as any;
        const isNewUser = userCredential._tokenResponse?.isNewUser || userCredential.additionalUserInfo?.isNewUser;
        console.log("[UserAuth] Is new user:", isNewUser);

        if (isNewUser) {
            console.log("[UserAuth] Handling new user registration...");
            await this.handleNewSocialUser(result.user);
            console.log("[UserAuth] New user registration completed successfully");
        } else {
            console.log("[UserAuth] Existing user, no registration needed");
        }
    }





    /**
     * Sign in with Facebook
     */
    async signInWithFacebook(): Promise<void> {
        try {
            this.beginLoading();
            const provider = new fAuth.FacebookAuthProvider();
            provider.addScope('email');
            provider.addScope('public_profile');

            console.log("[UserAuth] Using Firebase Facebook Auth");
            const result = await fAuth.signInWithPopup(this.auth, provider);

            console.log("Signed in with Facebook:", result.user);

            // Check if this is a new user
            const userCredential = result as any;
            const isNewUser = userCredential._tokenResponse?.isNewUser || userCredential.additionalUserInfo?.isNewUser;
            console.log("[UserAuth] Is new user:", isNewUser);

            if (isNewUser) {
                console.log("[Welcome] Handling new user registration...");
                await this.handleNewSocialUser(result.user);
                console.log("[Welcome] New user registration completed successfully");
            } else {
                console.log("[Welcome] Existing user, no registration needed");
            }
        } catch (error: any) {
            this.closeLoading();
            console.error("Facebook sign-in error:", error);

            if (error && error.code) {
                if (error.code === 'auth/popup-closed-by-user') {
                    return; // User cancelled
                } else if (error.code === 'auth/popup-blocked') {
                    presentSnackBar("Pop-up was blocked. Please allow pop-ups for this site.", SnackBarColor.DANGER, this.matSnackBar, "", 5000);
                } else if (error.code === 'auth/account-exists-with-different-credential') {
                    presentSnackBar("An account already exists with the same email address but different sign-in credentials.", SnackBarColor.DANGER, this.matSnackBar, "", 5000);
                } else {
                    presentSnackBar("There was an error signing in with Facebook", SnackBarColor.DANGER, this.matSnackBar, "", 5000);
                    throw error;
                }
            } else {
                console.log("Facebook sign-in completed (possibly new user)");
                return;
            }
        }
    }

    /**
     * Handle new user registration from social sign-in
     */
    private async handleNewSocialUser(user: fAuth.User): Promise<void> {
        try {
            console.log("[UserAuth] Creating new user profile for social sign-in:", user.email);

            // Create a basic user profile from social data
            const userId = generateId();
            const userSettingsComplete: UserSettingsComplete = {
                user: {
                    id: userId,
                    password: "", // No password for social users
                    firstName: user.displayName?.split(' ')[0] || "",
                    lastName: user.displayName?.split(' ').slice(1).join(' ') || "",
                    displayName: user.displayName || "",
                    authUid: user.uid,
                    profileIconId: null,
                    lastLanguageId: this.currentLang,
                    friendCode: this.afs.createId(),
                    email: user.email || "",
                    createdAt: getCurrentDateForDB(),
                    registeredAt: getCurrentDateForDB(),
                    siteRole: SiteRoleEnum.USER
                },
                userPushSettings: {
                    id: generateId(),
                    userId: userId, // Reference the actual user ID
                    enabled: true,
                    componentValueUpdates: true
                },
                // userDevice will be set by the backend
            };

            console.log("[UserAuth] User profile created, calling backend signUp...");

            try {
                // Create the user on the backend
                await this.signUp(userSettingsComplete, true, true); // Suppress error popups during verification, mark as social user
                console.log("[UserAuth] New user successfully created on backend");
                presentSnackBar("Account created successfully!", SnackBarColor.SUCCESS, this.matSnackBar, "", 3000);
            } catch (signUpError) {
                console.error("[UserAuth] SignUp failed, but checking if user was actually created...");
                console.log("[UserAuth] This is normal - some backends return errors even on success");

                // Check if the user was actually created despite the error
                // This can happen when backends return errors even on success
                try {
                    console.log("[UserAuth] Attempting to verify user creation...");
                    // Try to get the user from the server to see if creation actually succeeded
                    const userComplete = await this.getUserCompleteFromServer();
                    if (userComplete && userComplete.user) {
                        console.log("[UserAuth] ✅ User was actually created successfully despite error response");
                        // Small delay to ensure any previous popups are cleared
                        setTimeout(() => {
                            presentSnackBar("Account created successfully!", SnackBarColor.SUCCESS, this.matSnackBar, "", 3000);
                        }, 100);
                        return; // Success - don't throw error
                    }
                } catch (verificationError) {
                    console.log("[UserAuth] ❌ User verification failed, user was not created");
                }

                // If we get here, the user was not created, so re-throw the original error
                console.log("[UserAuth] User creation genuinely failed, re-throwing error");
                throw signUpError;
            }
        } catch (error) {
            console.error("[UserAuth] Error creating social user:", error);

            // If user creation fails, sign them out
            try {
                await this.signOut();
                console.log("[UserAuth] User signed out after failed creation");
            } catch (signOutError) {
                console.error("[UserAuth] Error signing out after failed creation:", signOutError);
            }

            // Re-throw the error with more context
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            throw new Error(`Failed to create user profile: ${errorMessage}`);
        }
    }

    signOut() {
        console.log('[UserAuth] signOut called, cleaning up data and connections');

        // Leave all lab rooms
        for (let i = 0; i < this.currentUserComplete?.labsComplete?.length; i++) {
            this.labService.leaveLabRoom(this.currentUserComplete.labsComplete[i].lab.id);
        }

        // Clear lab service state to prevent stale data issues
        this.labService.clearAllData();

        // Also clear gadget service data
        if (this.gadgetService && typeof this.gadgetService.clearAllData === 'function') {
            this.gadgetService.clearAllData();
        }

        // CRITICAL: Clear currentUserComplete to prevent stale user data
        console.log('[UserAuth] Clearing currentUserComplete to prevent stale user data');
        this.currentUserComplete = null;
        this.loggedIn = false;

        // Clear any pending redirect route
        this.pendingRedirectRoute = null;

        changePage(PagesEnum.WELCOME, this.router);
        return new Promise<void>((resolve, reject) => {
            fAuth.signOut(this.auth).finally(() => {
                console.log('[UserAuth] Firebase signOut completed');
                resolve();
            })
        })
    }

    createEmptyUser(activeLanguageId: LanguageEnum) {
        let user: User = {
            id: generateId(),
            password: "",
            firstName: "",
            lastName: "",
            displayName: "",
            authUid: null,
            profileIconId: null,
            lastLanguageId: activeLanguageId,
            friendCode: this.afs.createId(),
            email: "",
            createdAt: getCurrentDateForDB(),
            registeredAt: null,
            siteRole: SiteRoleEnum.USER
        }
        return user;
    }

    signUp(userSettingsComplete: UserSettingsComplete, suppressErrorPopups: boolean = false, isSocialUser: boolean = false) {
        this.beginLoading();

        return new Promise<void>((resolve, reject) => {

            console.log("[UserAuth] Sending user creation request to backend:", {
                userEmail: userSettingsComplete.user.email,
                userAuthUid: userSettingsComplete.user.authUid,
                userDisplayName: userSettingsComplete.user.displayName,
                isSocialUser: isSocialUser
            });


            // Log the full payload structure for debugging
            console.log("[UserAuth] Full payload structure:", JSON.stringify(userSettingsComplete, null, 2));

            this.http.post(connLink + "/createUser", { userSettingsComplete, isSocialUser }).subscribe(
                (response: any) => {
                    console.log("[UserAuth] User creation response:", response);
                    this.closeLoading();
                    resolve();
                },
                (error) => {
                    this.closeLoading();
                    console.error("[UserAuth] User creation failed:", error);

                    // Log detailed error information
                    if (error.status) {
                        console.log("[UserAuth] HTTP Status:", error.status);
                    }
                    if (error.error) {
                        console.log("[UserAuth] Error details:", error.error);
                    }
                    if (error.message) {
                        console.log("[UserAuth] Error message:", error.message);
                    }

                    // Only show error popups if not suppressed (for verification phase)
                    if (!suppressErrorPopups) {
                        // Handle different HTTP status codes
                        if (error.status === 400) {
                            console.log("[UserAuth] Bad Request (400) - likely validation error");
                            let failedResponse: FailedResponse = error.error;
                            if (failedResponse && failedResponse.apiErrorCode) {
                                console.log("[UserAuth] API Error Code:", failedResponse.apiErrorCode);
                                // The apiErrorCode enum values are already user-friendly messages
                                // e.g., "There is already an account signed up under this email!"
                                presentSnackBar(failedResponse.apiErrorCode, SnackBarColor.DANGER, this.matSnackBar);
                            } else {
                                console.log("[UserAuth] No specific API error code found");
                                presentSnackBar("Validation error: Please check your information", SnackBarColor.DANGER, this.matSnackBar);
                            }
                        } else if (error.status === 409) {
                            console.log("[UserAuth] Conflict (409) - user might already exist");
                            presentSnackBar("User account already exists", SnackBarColor.DANGER, this.matSnackBar);
                        } else if (error.status >= 500) {
                            console.log("[UserAuth] Server error (5xx)");
                            presentSnackBar("Server error, please try again later", SnackBarColor.DANGER, this.matSnackBar);
                        } else {
                            console.log("[UserAuth] Other error status");
                            presentSnackBar("Failed to create user account", SnackBarColor.DANGER, this.matSnackBar);
                        }
                    } else {
                        console.log("[UserAuth] Error popups suppressed for verification phase");
                    }

                    reject(error); // Pass the actual error
                }
            )
        })
    }

    beginLoading() {
        if (this.currentLoadingRef == null) {
            console.log("opening load")
            this.currentLoadingRef = presentLoading(this.matDialog);
        }
    }

    closeLoading() {

        if (this.currentLoadingRef != null) {
            console.log("closing load")
            this.currentLoadingRef.close();
            this.currentLoadingRef = null
        }
    }

    sendPasswordResetEmail(email: string) {
        return new Promise<void>((resolve, reject) => {
            return fAuth.sendPasswordResetEmail(this.auth, email).then(() => {
                presentSnackBar("A password reset link has been sent to your email!", SnackBarColor.SUCCESS, this.matSnackBar);
                resolve();
            })
                .catch(err => {
                    presentSnackBar("There was an error when trying to send a password recovery link to your email!", SnackBarColor.DANGER, this.matSnackBar);
                    console.log(err);
                    reject();
                })
        })

    }

    createIdToken(forceRefresh = false) {
        return new Promise((resolve, reject) => {
            if (!this.auth || !this.auth.currentUser) {
                reject(new Error('User is not authenticated'));
                return;
            }
            fAuth.getIdToken(this.auth.currentUser, forceRefresh).then(idToken => {
                resolve(idToken)
            }).catch(err => {
                console.log(err);
                reject(err);
            })
        })
    }

    testConnection() {
        console.log("trying to connect to: " + connLink + "/testConnection")
        this.http.post(connLink + "/testConnection", {}).subscribe(
            (response: any) => {
                console.log(response);
                presentSnackBar("Success", SnackBarColor.SUCCESS, this.matSnackBar)
            },
            (response) => {
                console.log(response);
                presentSnackBar("Failure", SnackBarColor.DANGER, this.matSnackBar)


            })
    }

    wipeUserData(newOwnerId?: string) {
        return new Promise<void>(async (resolve, reject) => {
            let idToken = await this.createIdToken();
            this.http.post(connLink + "/wipeUserData", {
                uid: this.currentUserComplete.user.authUid,
                idToken,
                newOwnerId
            }).subscribe(
                (response: any) => {
                    console.log(response);
                    resolve();
                },
                (response: any) => {
                    let failedResponse: FailedResponse = response.error;
                    console.log("failed response", failedResponse);
                    if (failedResponse.apiErrorCode) {
                        presentSnackBar(failedResponse.apiErrorCode, SnackBarColor.DANGER, this.matSnackBar);
                    }
                    reject();
                }
            )
        })
    }

    getLabMembersForUser() {
        return new Promise<any>(async (resolve, reject) => {
            if (!this.currentUserComplete || !this.currentUserComplete.user) {
                reject(new Error('User not authenticated or user data not loaded'));
                return;
            }

            let idToken = await this.createIdToken();
            this.http.post(connLink + "/getLabMembersForUser", {
                uid: this.currentUserComplete.user.authUid,
                idToken
            }).subscribe(
                (response: any) => {
                    console.log("Lab members response:", response);
                    resolve(response);
                },
                (response: any) => {
                    let failedResponse: FailedResponse = response.error;
                    console.log("failed response", failedResponse);
                    if (failedResponse.apiErrorCode) {
                        presentSnackBar(failedResponse.apiErrorCode, SnackBarColor.DANGER, this.matSnackBar);
                    }
                    reject();
                }
            )
        })
    }

}